import type { ReactNode } from 'react';

import { Link } from '../../../components/ui/Link';
import { Tag } from '../../../components/ui/Tag';
import type { Coupon, CouponGroup } from '../../coupon-management/versions/v1/fixtures';
import { hasExternalRange, relatedCoupons } from '../../coupon-management/versions/v1/stackRules';
import styles from './StackRangeField.module.css';

/**
 * 「叠加生效范围」只读块。
 *
 * 叠加关系在这里**不可编辑**——按产品约定，单券规则（全局 / 指定）与组合规则统一
 * 在「叠加规则设置」里配，这一侧只显示结果并给「查看全部 / 去设置」两个出口。
 * 优惠券编辑页与电子推荐卡的奖励设置都用它，避免两处文案与形态跑偏。
 */
export interface StackRangeFieldProps {
  /** 被查看的券：优惠券本体，或电子推荐卡派生出来的老生券／新生券。未创建时可不传。 */
  coupon?: Coupon;
  entities: readonly Coupon[];
  groups: readonly CouponGroup[];
  onOpenRelation: (id: number) => void;
  onOpenRules: (id: number) => void;
  /** 实体还没创建（推荐卡未保存、优惠券未保存）时只给提示，不给入口。 */
  unsaved?: boolean;
  unsavedHint?: ReactNode;
}

export function StackRangeField({ coupon, entities, groups, onOpenRelation, onOpenRules, unsaved = false, unsavedHint }: StackRangeFieldProps) {
  const related = coupon ? relatedCoupons(coupon.id, entities, groups) : [];
  const visibleRelated = related.slice(0, 5);
  const hiddenRelatedCount = related.length - visibleRelated.length;
  const external = coupon ? hasExternalRange(coupon, related, entities, groups) : false;

  if (unsaved || !coupon) {
    return <span className={`${styles.muted} ${styles.newCouponStackHint}`}>{unsavedHint ?? '优惠券创建后，可前往「叠加规则设置」设置叠加范围。'}</span>;
  }

  const note = external
    ? (coupon.stackMode !== 'none'
      ? '除本券设置外，部分叠加范围由其他优惠券的叠加设置或组内相互叠加规则产生。'
      : '本券未主动设置叠加范围，但因其他优惠券设置了与本券叠加，或本券已加入组内相互叠加规则，因此仍可与以上优惠券叠加。')
    : '';

  return <div className={styles.effectiveRange}>
    <div className={styles.effectiveHeader}>
      <div className={styles.effectiveLabel}>可叠加的优惠券：</div>
      <div className={styles.effectiveActions}>
        {related.length > 0 ? <Link onClick={() => onOpenRelation(coupon.id)}>查看全部</Link> : null}
        <Link onClick={() => onOpenRules(coupon.id)}>去设置</Link>
      </div>
    </div>
    {related.length > 0
      ? <div className={styles.effectiveCoupons}>
        {visibleRelated.map((item) => <Tag key={item.id} type="neutral">{item.name}</Tag>)}
        {hiddenRelatedCount > 0 ? <span className={styles.effectiveRemaining}>+{hiddenRelatedCount}</span> : null}
      </div>
      : <span className={styles.muted}>暂无可叠加优惠券</span>}
    {note ? <p className={styles.effectiveNote}>{note}</p> : null}
  </div>;
}
